/**
 * Guscio delle viste senza squadra: giocatori, competizioni e amministrazione
 * della societa, e le viste di piattaforma del Super Admin (§1.1, §1.6).
 *
 * Separato da quello operativo perche qui non c'e una squadra-stagione da
 * scegliere: anagrafiche, stagioni, squadre e incroci sono della societa
 * intera, e la piattaforma non ha societa. Stesso menu, ridotto alle voci
 * fuori dalla squadra, e in testa il ritorno alle squadre al posto del
 * selettore, per chi ha una societa. Chi apre un percorso senza il permesso
 * vede la stessa pagina di una vista inesistente.
 *
 * Mister e Dirigente qui non hanno voci: aprono la scheda di un giocatore o
 * lo storico dell'assistente, e un menu vuoto li lascerebbe senza strada.
 * Per loro la vista sta nel guscio dell'ultima squadra aperta, con il suo
 * menu, il selettore e la barra inferiore.
 */

import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, MenuIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { useContesto } from './contesto.js';
import { Guscio, Menu } from './Guscio.js';
import { NonTrovata } from './NonTrovata.js';
import { squadraDiRiferimento } from './ultimaSquadra.js';
import { menu, sezioneDi, voceAperta } from './voci.js';

export function GuscioSocieta() {
  const { utente, squadre } = useContesto();
  const { pathname } = useLocation();
  const [menuAperto, setMenuAperto] = useState(false);

  useEffect(() => setMenuAperto(false), [pathname]);

  const voce = voceAperta(pathname, utente);
  if (!voce) return <NonTrovata />;

  const sezioni = menu(utente);
  if (sezioni.length === 0) {
    if (!squadre) return <p className="attesa">Carico…</p>;
    const riferimento = squadraDiRiferimento(squadre);
    if (riferimento) return <Guscio squadra={riferimento.id} />;
  }
  const testa = (
    <div className="flex flex-col items-start gap-1 px-4 pt-4 pb-2">
      {utente.societaId && (
        <Button asChild variant="ghost" size="sm" className="-ml-2 text-muted-foreground">
          <Link to="/">
            <ArrowLeftIcon data-icon="inline-start" />
            Torna alle squadre
          </Link>
        </Button>
      )}
      <strong className="text-lg">{sezioneDi(voce)}</strong>
    </div>
  );

  return (
    <div className="min-h-dvh md:grid md:grid-cols-[260px_1fr]">
      <header className="sticky top-0 z-30 flex min-h-14 items-center gap-1 border-b-2 border-foreground bg-background px-2 pt-[env(safe-area-inset-top)] md:hidden">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMenuAperto(true)}
          aria-label="Apri il menu"
        >
          <MenuIcon />
        </Button>
        <span className="truncate text-[17px] font-semibold">{voce.etichetta}</span>
      </header>

      <aside className="sticky top-0 hidden h-dvh border-r-2 border-foreground md:block">
        <Menu sezioni={sezioni} testa={testa} />
      </aside>

      <Sheet open={menuAperto} onOpenChange={setMenuAperto}>
        <SheetContent
          side="left"
          showCloseButton={false}
          aria-describedby={undefined}
          className="w-[min(300px,85vw)] gap-0 border-r-2 border-foreground p-0 md:hidden"
        >
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <Menu sezioni={sezioni} testa={testa} onChiudi={() => setMenuAperto(false)} />
        </SheetContent>
      </Sheet>

      <main className="@container min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
